import React, { useEffect, useMemo, useState } from "react";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

const FALLBACK_DATA = [
  {
    id: "reg_001",
    eventName: "School Wars 2026",
    categoryName: "Fortnite Solo",
    registrationType: "individual",
    status: "confirmed",
    participant: "KuroNinja",
    email: "player1@example.com",
    createdAt: "2026-02-21T19:10:00.000Z",
  },
  {
    id: "reg_002",
    eventName: "Metro City Kumite",
    categoryName: "MK1 Open",
    registrationType: "team",
    status: "waitlist",
    participant: "Team BoricuaFGC",
    email: "captain@example.com",
    createdAt: "2026-02-21T19:12:00.000Z",
  },
];

const statusStyles = {
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
  waitlist: "bg-amber-500/20 text-amber-300 border-amber-400/30",
  pending: "bg-sky-500/20 text-sky-300 border-sky-400/30",
  checked_in: "bg-violet-500/20 text-violet-300 border-violet-400/30",
  no_show: "bg-rose-500/20 text-rose-300 border-rose-400/30",
  disqualified: "bg-rose-500/20 text-rose-300 border-rose-400/30",
};

function extractNoteField(notes, label) {
  const match = String(notes || "").match(new RegExp(`^${label}:\\s*(.+)$`, "im"));
  return match ? match[1].trim() : "";
}

function extractOperationalMeta(notes) {
  return {
    discord: extractNoteField(notes, "Discord"),
    game: extractNoteField(notes, "Game"),
  };
}

export default function AdminRegistrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function loadFromSupabase() {
      const { data, error } = await supabase
        .from("registrations")
        .select(`
          id,
          registration_type,
          status,
          created_at,
          event_categories (
            name,
            events ( name )
          ),
          notes,
          participants ( display_name, email, phone, city ),
          teams (
            name,
            captain_participant_id,
            participants!teams_captain_participant_id_fkey ( display_name, email, phone, city )
          )
        `)
        .order("created_at", { ascending: false })
        .limit(300);

      if (error) throw error;

      return (data || []).map((row) => {
        const participantMeta = row.registration_type === "team" ? row.teams?.participants : row.participants;
        const operationalMeta = extractOperationalMeta(row.notes);

        return {
          id: row.id,
          eventName: row.event_categories?.events?.name || "Evento",
          categoryName: row.event_categories?.name || "Categoría",
          registrationType: row.registration_type,
          status: row.status,
          participant: row.registration_type === "team" ? row.teams?.name : row.participants?.display_name,
          email: participantMeta?.email,
          phone: participantMeta?.phone || "",
          city: participantMeta?.city || "",
          notes: row.notes || "",
          operationalMeta,
          createdAt: row.created_at,
        };
      });
    }

    async function load() {
      try {
        if (hasSupabaseConfig && supabase) {
          const mapped = await loadFromSupabase();
          if (!cancelled) setRows(mapped);
          return;
        }

        const res = await fetch("/api/admin/registrations");
        if (!res.ok) throw new Error("api not available");
        const json = await res.json();
        if (!cancelled) setRows(Array.isArray(json) ? json : json.items || []);
      } catch {
        if (!cancelled) setRows(FALLBACK_DATA);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const q = query.toLowerCase();
      const matchesQuery =
        !q ||
        (r.id || "").toLowerCase().includes(q) ||
        (r.eventName || "").toLowerCase().includes(q) ||
        (r.categoryName || "").toLowerCase().includes(q) ||
        (r.participant || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.phone || "").toLowerCase().includes(q) ||
        (r.city || "").toLowerCase().includes(q);
      const matchesStatus = status === "all" || r.status === status;
      return matchesQuery && matchesStatus;
    });
  }, [rows, query, status]);

  function escapeCsv(value) {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  }

  function downloadCsv() {
    const headers = [
      "registro_id",
      "evento",
      "categoria",
      "tipo_registro",
      "participante_o_team",
      "contacto",
      "telefono",
      "ciudad",
      "estado",
      "fecha",
    ];

    const lines = [
      headers.join(","),
      ...filtered.map((row) =>
        [
          row.id,
          row.eventName,
          row.categoryName,
          row.registrationType,
          row.participant,
          row.email,
          row.phone,
          row.city,
          row.status,
          row.createdAt ? new Date(row.createdAt).toISOString() : "",
        ]
          .map(escapeCsv)
          .join(",")
      ),
    ];

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `registros-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-[#06070A] text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Admin · Registros</h1>
            <p className="text-slate-300 mt-1">Vista operativa de participantes/equipos por evento.</p>
          </div>
          <div className="flex gap-2">
            <a href="/admin/dashboard" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 w-fit">Dashboard</a>
            <a href="/admin/gamergy" className="px-4 py-2 rounded-lg border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10 w-fit">Gamergy Ops</a>
            <a href="/" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 w-fit">Volver al Home</a>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111722] p-4 md:p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por evento, gamer tag, email o id..."
            className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2 outline-none"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2 outline-none"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="waitlist">Waitlist</option>
            <option value="checked_in">Checked-in</option>
            <option value="no_show">No-show</option>
          </select>
          <button onClick={downloadCsv} className="rounded-lg bg-cyan-400 text-black font-semibold px-3 py-2">Exportar CSV</button>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f1420] text-slate-300">
                <tr>
                  <th className="text-left px-4 py-3">Registro</th>
                  <th className="text-left px-4 py-3">Evento</th>
                  <th className="text-left px-4 py-3">Categoría</th>
                  <th className="text-left px-4 py-3">Participante/Team</th>
                  <th className="text-left px-4 py-3">Contacto</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-slate-300">Cargando registros...</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-slate-300">No hay registros para estos filtros.</td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id} className="border-t border-white/10">
                      <td className="px-4 py-3 text-slate-200">{row.id}</td>
                      <td className="px-4 py-3">{row.eventName}</td>
                      <td className="px-4 py-3">{row.categoryName}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-100">{row.participant}</div>
                      {row.operationalMeta?.game ? <div className="text-xs text-slate-400 mt-1">{row.operationalMeta.game}</div> : null}
                    </td>
                      <td className="px-4 py-3 text-slate-300">
                        <div>{row.email || "—"}</div>
                        {row.phone ? <div className="text-xs text-slate-400 mt-1">{row.phone}</div> : null}
                        {row.city ? <div className="text-xs text-slate-500 mt-1">{row.city}</div> : null}
                        {row.operationalMeta?.discord ? <div className="text-xs text-slate-500 mt-1">Discord: {row.operationalMeta.discord}</div> : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2.5 py-1 rounded-full border ${statusStyles[row.status] || "bg-slate-500/20 text-slate-300 border-slate-400/30"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{new Date(row.createdAt).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
